import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import {WorkingSureFlowLoop} from '../../model/working-sure-flow-loop';
import {PersonLogin} from '../../person/person-login';
import {SureFlowLoopService} from '../../services/sure-flow-loop.service';
import {StringUtils} from '../../utils/string-utils';
import {SureFlowLoopDetail} from '../../model/sure-flow-loop-detail';
import {PersonLoginService} from '../../services/person-login-service';
import {SureFlowLoopInitial} from '../../model/sure-flow-loop-initial';
import { Router } from '@angular/router';
import {SureFlowUnitService} from '../../services/sure-flow-unit.service';
import {WorkingSureFlowUnit} from '../../model/working-sure-flow-unit';
import {DialogService} from '../../services/dialog.service';
import {SureFlowUnitData} from '../../model/sure-flow-unit-data';
import {SureFlowAutoSelectInput} from '../../model/sure-flow-auto-select-input';
import {forkJoin, Observable} from 'rxjs';
import {SureFlowRatingService} from '../../services/sure-flow-rating.service';
import {SureFlowWorkingRatingInput} from '../../model/sure-flow-working-rating-input';
import {SureFlowUnitConversionService} from '../../services/sure-flow-unit-conversion.service';
import {RatingUnitUtils} from '../../utils/rating-unit-utils';
import {UserStateService} from '../../services/user-state.service';
import {SureflowUnitUtils} from '../../utils/sureflow-unit-utils';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {SureflowLoopDesignerComponent} from '../sureflow-loop-designer/sureflow-loop-designer.component';


@Component({
    selector: 'app-working-sure-flow-loops',
    templateUrl: './working-sure-flow-loops.component.html',
    styleUrls: ['./working-sure-flow-loops.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SureflowLoopDesignerComponent, MatProgressSpinner]
})
export class WorkingSureFlowLoopsComponent implements OnChanges, OnInit {
  private readonly service = inject(SureFlowLoopService);
  private readonly unitService = inject(SureFlowUnitService);
  private readonly router = inject(Router);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly sureFlowRatingService = inject(SureFlowRatingService);
  private readonly dialogService = inject(DialogService);
  private readonly sureFlowConversionService = inject(SureFlowUnitConversionService);
  private readonly userStateService = inject(UserStateService);
  readonly loopId = input.required<string>();
  private readonly workingLoops = signal<WorkingSureFlowLoop[]>(undefined!);
  private readonly selectedWorkingLoopId = signal<number>(undefined!);
  readonly selectedLoopId = signal<number>(0);
  readonly selectedLoopUnits = signal<WorkingSureFlowUnit[]>(undefined!);
  readonly selectedUnitIds = signal<number[]>([]);
  readonly loops = signal<SureFlowLoopDetail[]>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  private personLogin!: PersonLogin;

  ngOnInit(): void {
    this.initialize();
    this.personLogin = this.personLoginService.getCachedLogin()!;
    this.loadLoopList();
  }

  private loadLoopList(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.service.getWorkingLoops(this.personLogin.person.personId).subscribe({
      next: (loops) => {
        this.workingLoops.set(loops);
        this.loops.set(this.workingLoops().flatMap(loop => loop.detail));
        if (this.selectedWorkingLoopId() > 0) {
          this.selectedLoopId.set(this.workingLoops().filter(loop => loop.id === this.selectedWorkingLoopId())[0]?.detail?.id || 0);
          this.unitService.getWorkingLoopUnits(this.selectedWorkingLoopId()).subscribe({
            next: (units) => {
              this.selectedLoopUnits.set(units);
              this.isBusy.set(false);
            }, error: (error) => this.handleError(error)
          });
        } else {
          this.selectedLoopId.set(0);
          this.isBusy.set(false);
        }
      }, error: (error) => this.handleError(error)
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.formatErrorMessage(error));
  }

  addLoop(input: SureFlowLoopInitial): void {
    if (StringUtils.isNotBlank(input?.systemTypeCode)) {
      this.isBusy.set(true);
      const detail: SureFlowLoopDetail = SureflowUnitUtils.createLoopDetail(input);
      const loop: WorkingSureFlowLoop = new WorkingSureFlowLoop();
      loop.personId = this.personLogin.person.personId;
      loop.orgUnitId = this.personLogin.person.primaryOrgUnitId;
      loop.detail = detail;
      this.service.saveWorkingLoop(loop).subscribe({
        next: (inserted) => {
          this.changeWorkingLoopId(inserted.id);
          this.loadLoopList();
        },
        error: (error) => this.handleError(error)
      });
    }
  }

  deleteLoop(input: SureFlowLoopDetail): void {
    const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(input.id);
    this.service.deleteWorkingLoop(workingLoop.id).subscribe({
      next: () => {
        this.changeWorkingLoopId(0);
        this.loadLoopList();
      }, error: (error) => this.handleError(error),
    });
  }

  private getWorkingLoop(detailId: number): WorkingSureFlowLoop {
    const filtered = this.workingLoops().filter(loop => loop.detail.id === detailId);
    if (filtered.length === 1) {
      return JSON.parse(JSON.stringify(filtered[0]));
    }
    return undefined!;
  }

  addUnit(detail: SureFlowLoopDetail): void {
    const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(detail.id);
    this.router.navigate(['rating', 'sureFlowUnit', workingLoop.id, 0]);
  }

  editUnits(): void {
    const units = this.getSelectedUnits();
    const unitCount = units?.length || 0;
    if (unitCount === 1) {
      const unit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(units[0]));
      if (unit.unitData?.sureFlowRatingUnit) {
        this.router.navigate(['rating', 'sureFlowUnit', unit.loopId, unit.id]);
      } else {
        const workingLoop: WorkingSureFlowLoop = this.getWorkingLoopById(unit.loopId);
        this.dialogService.showAddNonSureFlowUnitDialog(workingLoop.detail.systemTypeCode, unit.unitData).subscribe({
          next: (input) => this.updateExternalUnit(input, unit, workingLoop),
          error: (error) => this.handleError(error),
        });
      }
    } else if (unitCount > 1) {
      const ratingRepresentative = units.filter(unit => (unit.unitData?.sureFlowRatingUnit?.id || 0) > 0);
      const representative: SureFlowUnitData = JSON.parse(JSON.stringify(ratingRepresentative.length > 0 ? ratingRepresentative[0].unitData : units[0].unitData));
      const ratingUnit = ratingRepresentative.length > 0 ? JSON.parse(JSON.stringify(ratingRepresentative[0].unitData.sureFlowRatingUnit)) : undefined;
      const workingLoop: WorkingSureFlowLoop = this.getWorkingLoopById(units[0].loopId);
      this.dialogService.showBulkEditSureFlowUnitsDialog(workingLoop.detail.systemTypeCode, representative, ratingUnit, workingLoop.detail.metric).subscribe({
        next: (updated: WorkingSureFlowUnit) => {
          if (updated) {
            this.isBusy.set(true);
            updated.id = 0;
            updated.unitData.active = true;
            updated.unitData.id = 0;
            RatingUnitUtils.clearSureFlowRatingIds(updated.unitData);
            const updatedUnits: WorkingSureFlowUnit[] = [];
            let sequence: number = 1;
            const updatedIds: number[] = units.map(unit => unit.id);
            for (const unit of this.selectedLoopUnits()) {
              unit.sequence = sequence++;
              if (updatedIds.indexOf(unit.id) >= 0) {
                const updatedClone: WorkingSureFlowUnit = JSON.parse(JSON.stringify(updated));
                updatedClone.unitData.active = true;
                updatedClone.unitData.userTag = unit.unitData.userTag;
                updatedClone.id = unit.id;
                updatedClone.unitData.id = unit.unitData.id;
                updatedClone.sequence = unit.sequence;
                if (unit.unitData?.sureFlowRatingUnit) {
                  if (!updatedClone.unitData.sureFlowRatingUnit) {
                    updatedClone.unitData.sureFlowRatingUnit = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit));
                  } else {
                    updatedClone.unitData.sureFlowRatingUnit.id = unit.unitData.sureFlowRatingUnit.id;
                  }
                  if (unit.unitData.sureFlowRatingUnit.request) {
                    if (!updatedClone.unitData.sureFlowRatingUnit.request) {
                      updatedClone.unitData.sureFlowRatingUnit.request = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.request));
                    } else {
                      updatedClone.unitData.sureFlowRatingUnit.request.id = unit.unitData.sureFlowRatingUnit.request.id;
                    }
                    if (unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest) {
                      if (!updatedClone.unitData.sureFlowRatingUnit.request.primaryCoilRequest) {
                        updatedClone.unitData.sureFlowRatingUnit.request.primaryCoilRequest = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest));
                      } else {
                        updatedClone.unitData.sureFlowRatingUnit.request.primaryCoilRequest.id = unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.id;
                      }
                    }
                    if (unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest) {
                      if (!updatedClone.unitData.sureFlowRatingUnit.request.secondaryCoilRequest) {
                        updatedClone.unitData.sureFlowRatingUnit.request.secondaryCoilRequest = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest));
                      } else {
                        updatedClone.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.id = unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.id;
                      }
                    }
                    if (unit.unitData.sureFlowRatingUnit.request.electricHeatRequest) {
                      if (!updatedClone.unitData.sureFlowRatingUnit.request.electricHeatRequest) {
                        updatedClone.unitData.sureFlowRatingUnit.request.electricHeatRequest = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.request.electricHeatRequest));
                      } else {
                        updatedClone.unitData.sureFlowRatingUnit.request.electricHeatRequest.id = unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.id;
                      }
                    }
                  }
                  if (unit.unitData.sureFlowRatingUnit.response) {
                    if (!updatedClone.unitData.sureFlowRatingUnit.response) {
                      updatedClone.unitData.sureFlowRatingUnit.response = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.response));
                    } else {
                      updatedClone.unitData.sureFlowRatingUnit.response.id = unit.unitData.sureFlowRatingUnit.response.id;
                    }
                    if (unit.unitData.sureFlowRatingUnit.response.circuitResponse) {
                      if (!updatedClone.unitData.sureFlowRatingUnit.response.circuitResponse) {
                        updatedClone.unitData.sureFlowRatingUnit.response.circuitResponse = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.response.circuitResponse));
                      } else {
                        updatedClone.unitData.sureFlowRatingUnit.response.circuitResponse.id = unit.unitData.sureFlowRatingUnit.response.circuitResponse.id;
                      }
                    }
                    if (unit.unitData.sureFlowRatingUnit.response.alternateCircuit) {
                      if (!updatedClone.unitData.sureFlowRatingUnit.response.alternateCircuit) {
                        updatedClone.unitData.sureFlowRatingUnit.response.alternateCircuit = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.response.alternateCircuit));
                      } else {
                        updatedClone.unitData.sureFlowRatingUnit.response.alternateCircuit.id = unit.unitData.sureFlowRatingUnit.response.alternateCircuit.id;
                      }
                    }
                    if (unit.unitData.sureFlowRatingUnit.response.primaryCoilResponse) {
                      if (!updatedClone.unitData.sureFlowRatingUnit.response.primaryCoilResponse) {
                        updatedClone.unitData.sureFlowRatingUnit.response.primaryCoilResponse = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.response.primaryCoilResponse));
                      } else {
                        updatedClone.unitData.sureFlowRatingUnit.response.primaryCoilResponse.id = unit.unitData.sureFlowRatingUnit.response.primaryCoilResponse.id;
                      }
                    }
                    if (unit.unitData.sureFlowRatingUnit.response.secondaryCoilResponse) {
                      if (!updatedClone.unitData.sureFlowRatingUnit.response.secondaryCoilResponse) {
                        updatedClone.unitData.sureFlowRatingUnit.response.secondaryCoilResponse = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.response.secondaryCoilResponse));
                      } else {
                        updatedClone.unitData.sureFlowRatingUnit.response.secondaryCoilResponse.id = unit.unitData.sureFlowRatingUnit.response.secondaryCoilResponse.id;
                      }
                    }
                    if (unit.unitData.sureFlowRatingUnit.response.electricHeatResponse) {
                      if (!updatedClone.unitData.sureFlowRatingUnit.response.electricHeatResponse) {
                        updatedClone.unitData.sureFlowRatingUnit.response.electricHeatResponse = JSON.parse(JSON.stringify(unit.unitData.sureFlowRatingUnit.response.electricHeatResponse));
                      } else {
                        updatedClone.unitData.sureFlowRatingUnit.response.electricHeatResponse.id = unit.unitData.sureFlowRatingUnit.response.electricHeatResponse.id;
                      }
                    }
                  }
                }
                updatedUnits.push(updatedClone);
              } else {
                updatedUnits.push(unit);
              }
            }
            const input: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(workingLoop, updatedUnits);
            this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(input).subscribe({
              next: () => {
                this.changeWorkingLoopId(workingLoop.id);
                this.loadLoopList();
              }, error: (error) => this.handleError(error)
            });
          }
        }, error: (error) => this.handleError(error)
      });
    }
  }

  private getWorkingLoopById(id: number): WorkingSureFlowLoop {
    const filtered = this.workingLoops().filter(loop => loop.id === id);
    if (filtered.length === 1) {
      return JSON.parse(JSON.stringify(filtered[0]));
    }
    return undefined!;
  }

  private updateExternalUnit(input: SureFlowUnitData, unitToUpdate: WorkingSureFlowUnit, workingLoop: WorkingSureFlowLoop): void {
    if (input) {
      if (!isNaN(input.coolingTargetTotalLoad) && input.coolingTargetTotalLoad > 0) {
        input.coolingTargetSensibleLoad = (workingLoop.detail.coolingTargetSensibleHeatRatio / 100) * input.coolingTargetTotalLoad;
      }
      const units: WorkingSureFlowUnit[] = [];
      let sequence: number = 1;
      for (const unit of this.selectedLoopUnits()) {
        if (unit.id === unitToUpdate.id) {
          const workingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(unitToUpdate));
          workingUnit.unitData = input;
          workingUnit.sequence = sequence++;
          units.push(workingUnit);
        } else {
          const workingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(unit));
          workingUnit.sequence = sequence++;
          units.push(workingUnit);
        }
      }
      this.isBusy.set(true);
      const ratingInput: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(workingLoop, units);
      this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(ratingInput).subscribe({
        next: () => this.loadLoopList(),
        error: (error) => this.handleError(error)
      });
    }
  }

  addNonSureFlowUnit(detail: SureFlowLoopDetail): void {
    const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(detail.id);
    const unitData: SureFlowUnitData = new SureFlowUnitData();
    unitData.active = true;
    unitData.userTag = 'External Unit';
    this.dialogService.showAddNonSureFlowUnitDialog(workingLoop.detail.systemTypeCode, unitData).subscribe({
      next: (input) => this.addExternalUnit(input, workingLoop),
      error: (error) => this.handleError(error),
    });
  }

  private addExternalUnit(input: SureFlowUnitData, workingLoop: WorkingSureFlowLoop): void {
    if (input) {
      this.isBusy.set(true);
      const units: WorkingSureFlowUnit[] = [];
      let sequence: number = 1;
      for (const unit of this.selectedLoopUnits()) {
        const workingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(unit));
        workingUnit.sequence = sequence++;
        units.push(workingUnit);
      }
      const workingUnit: WorkingSureFlowUnit = new WorkingSureFlowUnit();
      workingUnit.loopId = workingLoop.id;
      if (!isNaN(input.coolingTargetTotalLoad) && input.coolingTargetTotalLoad > 0) {
        input.coolingTargetSensibleLoad = (workingLoop.detail.coolingTargetSensibleHeatRatio / 100) * input.coolingTargetTotalLoad;
      }
      workingUnit.unitData = input;
      workingUnit.sequence = sequence++;
      units.push(workingUnit);
      const ratingInput: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(workingLoop, units);
      this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(ratingInput).subscribe({
        next: () => this.loadLoopList(),
        error: (error) => this.handleError(error)
      });
    }
  }

  changeSelectedLoopId(detail: SureFlowLoopDetail): void {
    this.userStateService.clearLoopDeltaValues();
    const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(detail.id);
    this.changeWorkingLoopId(workingLoop?.id || 0);
    this.selectedLoopId.set(detail?.id || 0);
  }

  private changeWorkingLoopId(newId: number): void {
    this.isBusy.set(true);
    this.router.navigate(['ratings', 'sureflowLoops', newId]);
    this.selectedWorkingLoopId.set(newId);
    if (newId > 0) {
      this.unitService.getWorkingLoopUnits(newId).subscribe({
        next: (units) => {
          this.selectedLoopUnits.set(units);
          this.isBusy.set(false);
        }, error: (error) => this.handleError(error)
      });
    }
  }

  deleteLoopUnits(): void {
    this.isBusy.set(true);
    const unitsToDelete = this.getSelectedUnits();
    if (unitsToDelete.length > 0) {
      const workingLoopId = unitsToDelete[0]?.loopId || 0;
      const selectedLoop = this.workingLoops().filter(loop => loop.id === workingLoopId)[0];
      const units: WorkingSureFlowUnit[] = [];
      let sequence = 1;
      for (const workingUnit of this.selectedLoopUnits()) {
        const removedFilter = unitsToDelete.filter(unitToRemove => unitToRemove.id === workingUnit.id);
        if (removedFilter.length === 0) {
          const unit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(workingUnit));
          unit.sequence = sequence++;
          units.push(unit);
        }
      }
      const deleteObservables: Observable<string>[] = [];
      for (const unitToDelete of unitsToDelete) {
        deleteObservables.push(this.unitService.deleteWorkingLoopUnit(unitToDelete.id));
      }
      forkJoin(deleteObservables).subscribe({
        next: () => {
          const input: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(selectedLoop, units);
          this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(input).subscribe({
            next: () => {
              this.loadLoopList();
              this.selectedUnitIds.set([]);
            },
            error: (error) => this.handleError(error)
          });
        }, error: (error) => this.handleError(error)
      });
    } else {
      this.isBusy.set(false);
    }
  }

  private getSelectedUnits(): WorkingSureFlowUnit[] {
    if ((this.selectedLoopUnits()?.length || 0) > 0 && (this.selectedUnitIds()?.length || 0) > 0) {
      return this.selectedLoopUnits().filter(unit => this.selectedUnitIds().indexOf(unit.id) >= 0);
    }
    return [];
  }

  copyLoopUnits(): void {
    this.isBusy.set(true);
    const unitsToCopy = this.getSelectedUnits();
    if (unitsToCopy.length > 0) {
      const workingLoopId = unitsToCopy[0]?.loopId || 0;
      const selectedLoop = this.workingLoops().filter(loop => loop.id === workingLoopId)[0];
      const units: WorkingSureFlowUnit[] = [];
      let sequence = 1;
      for (const workingUnit of this.selectedLoopUnits()) {
        const existingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(workingUnit));
        existingUnit.sequence = sequence++;
        units.push(existingUnit);
      }
      for (const unitToCopy of unitsToCopy) {
        const newUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(unitToCopy));
        newUnit.sequence = sequence++;
        newUnit.id = 0;
        RatingUnitUtils.clearSureFlowRatingIds(newUnit.unitData);
        newUnit.unitData.id = 0;
        units.push(newUnit);
      }
      const input: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(selectedLoop, units);
      this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(input).subscribe({
        next: () => this.loadLoopList(),
        error: (error) => this.handleError(error)
      });
    } else {
      this.isBusy.set(false);
    }
  }

  updateAndRecalculateLoop(detail: SureFlowLoopDetail): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(detail.id);
    workingLoop.detail = detail;
    const units: WorkingSureFlowUnit[] = [];
    let sequence = 1;
    for (const workingUnit of this.selectedLoopUnits()) {
      const existingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(workingUnit));
      existingUnit.sequence = sequence++;
      units.push(existingUnit);
    }
    const input: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(workingLoop, units);
    this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(input).subscribe({
      next: () => {
        this.changeWorkingLoopId(workingLoop.id);
        this.loadLoopList();
      }, error: (error) => this.handleError(error)
    });
  }

  autoSelectUnits(input: SureFlowAutoSelectInput): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(input.loopDetail.id);
    workingLoop.detail = input.loopDetail;
    input.loopId = workingLoop.id;
    this.sureFlowRatingService.autoSelectWorkingUnits(input).subscribe({
      next: () => {
        this.changeWorkingLoopId(workingLoop.id);
        this.loadLoopList();
      }, error: (error) => this.handleError(error)
    });
  }

  rearrangeUnits(detail: SureFlowLoopDetail): void {
    this.errorMessage.set(undefined!);
    const unitDetails: SureFlowUnitData[] = this.selectedLoopUnits().map(unit => unit.unitData);
    this.dialogService.showRearrangeSureFlowUnitsDialog(unitDetails).subscribe({
      next: (rearranged) => {
        if ((rearranged?.length || 0) > 0) {
          this.isBusy.set(true);
          const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(detail.id);
          const workingUnits = this.buildWorkingSureFlowUnitList(rearranged);
          const input: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(workingLoop, workingUnits);
          this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(input).subscribe({
            next: () => {
              this.changeWorkingLoopId(workingLoop.id);
              this.loadLoopList();
            }, error: (error) => this.handleError(error)
          });
        }
      }, error: (error) => this.handleError(error)
    });
  }

  private buildWorkingSureFlowUnitList(unitData: SureFlowUnitData[]): WorkingSureFlowUnit[] {
    const units: WorkingSureFlowUnit[] = [];
    let sequence = 1;
    for (const unit of unitData) {
      const workingUnit = this.selectedLoopUnits().filter(loopUnit => loopUnit.unitData.id === unit.id)[0];
      if (workingUnit) {
        workingUnit.sequence = sequence++;
        units.push(workingUnit);
      }
    }
    return units;
  }

  copyLoop(detail: SureFlowLoopDetail): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(detail.id);
    const workingUnits: WorkingSureFlowUnit[] = [];
    const clone: WorkingSureFlowLoop = JSON.parse(JSON.stringify(workingLoop));
    clone.id = 0;
    clone.detail.id = 0;
    clone.personId = this.personLogin.person.personId;
    clone.orgUnitId = this.personLogin.person.primaryOrgUnitId;
    let sequence: number = 1;
    for (const unit of this.selectedLoopUnits()) {
      const workingUnit: WorkingSureFlowUnit = JSON.parse(JSON.stringify(unit));
      workingUnit.sequence = sequence++;
      workingUnit.id = 0;
      workingUnit.loopId = 0;
      workingUnit.unitData.id = 0;
      RatingUnitUtils.clearSureFlowRatingIds(workingUnit.unitData);
      workingUnits.push(workingUnit);
    }
    const input: SureFlowWorkingRatingInput = new SureFlowWorkingRatingInput(clone, workingUnits);
    this.sureFlowRatingService.updateAndSaveWorkingLoopAndUnits(input).subscribe({
      next: () => {
        this.changeWorkingLoopId(0);
        this.loadLoopList();
      }, error: (error) => this.handleError(error)
    });
  }

  addLoopsToSchedule(): void {
    this.dialogService.showAddSureFlowLoopsToScheduleDialog(this.workingLoops()).subscribe({
      next: (input) => {
        if (input) {
          this.isBusy.set(true);
          this.errorMessage.set(undefined!);
          this.service.createSchedule(input).subscribe({
            next: (schedule) => this.router.navigate(['schedules', schedule.id.entityId]),
            error: (error) => this.handleError(error)
          });
        }
      }, error: (error) => this.handleError(error)
    });
  }

  saveLoopDescription(detail: SureFlowLoopDetail): void {
    const workingLoop: WorkingSureFlowLoop = this.getWorkingLoop(detail.id);
    workingLoop.detail.description = detail.description;
    this.isBusy.set(true);
    this.service.saveWorkingLoop(workingLoop).subscribe({
      next: () => {
        this.changeWorkingLoopId(workingLoop.id);
        this.loadLoopList();
        this.isBusy.set(false);
      }, error: (error) => this.handleError(error)
    });
  }

  unitSelectionChanged(units: WorkingSureFlowUnit[]): void {
    if ((units?.length || 0) > 0) {
      this.selectedUnitIds.set(units.map(unit => unit.id));
    } else {
      this.selectedUnitIds.set([]);
    }
  }


  private initialize(): void {
    const loopId = +this.loopId();
    if (isNaN(loopId)) {
      this.selectedWorkingLoopId.set(0);
    } else {
      this.selectedWorkingLoopId.set(loopId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['loopId'].firstChange) {
      this.initialize();
    }
  }
}
